import { COMPANY_STATUS } from "@kamae-apps/shared/Types/Company"
import { DEFAULT_LANGUAGE } from "@kamae-apps/shared/Types/Company"
import type { Company } from "@kamae-apps/shared/Types/Company"
import type User from "@kamae-apps/shared/Types/User"
import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react"
import { fetchUserData, updateUserData } from "./userUseCases"

interface UserContextValue {
  user: User
  loadUser: () => Promise<void>
  updateUser: (body: User) => Promise<void>
}

const initCompany: Company = {
  name: "Kamaé",
  status: COMPANY_STATUS.CUSTOMER,
  level: 0,
  creation: new Date(),
  global_score: 0,
  logo: "default.png",
  progress: 0,
  id: 0,
  total_user: 0,
  automated_phishing_activated: false,
  phishing_enabled: false,
  sso_enabled: false,
  lms_enabled: false,
  defaultLanguageId: DEFAULT_LANGUAGE.FR,
  dir_sync_enabled: false,
  hide_rankings: false,
}

const initContext: User = {
  avatar: "",
  first_name: "",
  last_name: "",
  level: 0,
  status: 1,
  team: {
    name: "",
    global_score: 0,
    level: 0,
    company: initCompany,
    id: 0,
    progress: 0,
    user_count: 0,
  },
  languageId: DEFAULT_LANGUAGE.FR,
  company: initCompany,
  id: 0,
  intercom_hash: "",
  global_score: 0,
  email: "kamae@kamae.io",
  job: "",
  progress: 0,
  is_lms_user: false,
}

const UserContext = createContext<UserContextValue | null>(null)

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(initContext)

  const loadUser = useCallback(async () => setUser(await fetchUserData()), [])
  const updateUser = useCallback(async (body: User) => setUser(await updateUserData(body)), [])

  const value = useMemo(() => ({ user, loadUser, updateUser }), [user, loadUser, updateUser])
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser() {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error("useUser must be used within <UserProvider>")
  return ctx
}
